import { useCallback, useState } from "react";

import { markAllAsRead, updateReadStatus } from "~/graphql/mutations";
import appsyncClient from "~/lib/appsyncClient";
import { withRetry } from "~/lib/graphqlRetry";

export const useNotificationStatus = (
  UserID,
  TeamID,
  Type,
  showOnlyUnread,
  setNotifications,
  adjustUnreadCount,
) => {
  const [updatingAllNotifications, setUpdatingAllNotifications] =
    useState(false);
  const [updatingNotificationIds, setUpdatingNotificationIds] = useState(
    new Set(),
  );
  const [error, setError] = useState(null);

  const clearError = useCallback(() => {
    setError(null);
  }, []);

  const markAllNotificationsAsRead = useCallback(async () => {
    setUpdatingAllNotifications(true);
    setError(null);

    try {
      const result = await withRetry(() =>
        appsyncClient.graphql({
          query: markAllAsRead,
          variables: {
            UserID,
            TeamID,
            Type,
          },
        }),
      );
      const response = result.data?.markAllAsRead;

      if (response?.Success) {
        // Update notifications in the list and calculate how many were unread
        setNotifications((prevNotifications) => {
          const unreadCount = prevNotifications.filter((n) => !n.IsRead).length;
          // Adjust count by negative of unread (effectively sets visible unread to 0)
          adjustUnreadCount(-unreadCount);

          if (showOnlyUnread) {
            // Clear the list when showing only unread
            return [];
          }
          // Mark all as read when showing all
          return prevNotifications.map((notification) => ({
            ...notification,
            IsRead: true,
          }));
        });
      }
    } catch (e) {
      console.error("Failed to mark all notifications as read:", e);
      setError("Failed to mark all as read. Please try again.");
    } finally {
      setUpdatingAllNotifications(false);
    }
  }, [
    UserID,
    TeamID,
    Type,
    showOnlyUnread,
    setNotifications,
    adjustUnreadCount,
  ]);

  const toggleNotificationReadStatus = useCallback(
    async (notification, IsRead) => {
      if (!notification) {
        return;
      }

      setUpdatingNotificationIds((prev) => new Set([...prev, notification.ID]));
      setError(null);

      try {
        const result = await withRetry(() =>
          appsyncClient.graphql({
            query: updateReadStatus,
            variables: {
              input: {
                ID: notification.ID,
                CreatedAt: notification.CreatedAt,
                IsRead,
                UserID: notification.UserID,
                TeamID: notification.TeamID,
              },
            },
          }),
        );

        const data = result.data?.updateReadStatus;

        if (data?.ID) {
          // Adjust unread count: -1 if marking as read, +1 if marking as unread
          adjustUnreadCount(IsRead ? -1 : 1);

          setNotifications((prevNotifications) => {
            const index = prevNotifications.findIndex((n) => n.ID === data.ID);

            if (index !== -1 && IsRead && showOnlyUnread) {
              const updatedNotifications = [...prevNotifications];
              updatedNotifications.splice(index, 1);

              return updatedNotifications;
            }
            if (index !== -1) {
              const updatedNotifications = [...prevNotifications];

              updatedNotifications[index] = {
                ...updatedNotifications[index],
                IsRead: data.IsRead,
              };

              return updatedNotifications;
            }

            return prevNotifications;
          });
        }
      } catch (e) {
        console.error("Failed to update notification read status:", e);
        setError("Failed to update notification. Please try again.");
      } finally {
        setUpdatingNotificationIds((prev) => {
          const updated = new Set(prev);
          updated.delete(notification.ID);

          return updated;
        });
      }
    },
    [showOnlyUnread, setNotifications, adjustUnreadCount],
  );

  return {
    updatingAllNotifications,
    updatingNotificationIds,
    error,
    clearError,
    markAllNotificationsAsRead,
    toggleNotificationReadStatus,
  };
};

export default useNotificationStatus;
